import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, memo } from 'react';

import { cn } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Tooltip } from '@/components/ui/Tooltip';

const getAccessibleText = (children: React.ReactNode): string | undefined => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    const text = children
      .map((child) => getAccessibleText(child as React.ReactNode))
      .filter((value): value is string => value != null)
      .join('');

    return text.length > 0 ? text : undefined;
  }

  return undefined;
};

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md font-semibold text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed min-w-32',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-white hover:bg-primary/90 active:bg-sidenav',
        secondary: 'bg-white text-primary border border-primary hover:bg-primary hover:text-white active:bg-sidenav',
        ghost: 'bg-transparent text-gray800 hover:text-primary active:text-primary',
        selected: 'bg-successFg text-white hover:bg-dealsGreen',
        danger: 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700',
      },
      size: {
        lg: 'py-3 px-3 rounded-lg',
        md: 'py-2 px-2 rounded-md',
        sm: 'py-1 px-2 rounded-sm text-xs',
      },
      disabled: {
        true: 'cursor-not-allowed border border-gray200 bg-gray50 text-gray400 disabled:border-gray200 disabled:bg-gray50 disabled:text-gray400 disabled:hover:border-gray200 disabled:hover:bg-gray50 disabled:hover:text-gray400 disabled:active:bg-gray50 disabled:[&>svg]:text-gray400',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      disabled: false,
    },
  }
);

const ButtonIcon = memo(
  ({ icon: Icon, className }: { icon: React.ComponentType<{ className?: string }>; className?: string }) => (
    <Icon className={className} />
  )
);

ButtonIcon.displayName = 'ButtonIcon';

export type ButtonProps = {
  loading?: boolean;
  disabled?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  tooltip?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export const Button: React.ForwardRefExoticComponent<ButtonProps & React.RefAttributes<HTMLButtonElement>> = forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ className, variant, size, loading, icon, iconClassName, disabled, tooltip, children, ...props }, ref) => {
  const buttonClassName = cn(buttonVariants({ variant, size, className, disabled }));
  const iconClassNames = cn('h-[17px] w-[17px] shrink-0', iconClassName);

  const buttonElement = (
    <button
      type="button"
      className={buttonClassName}
      ref={ref}
      disabled={disabled}
      aria-label={props['aria-label'] ?? getAccessibleText(children)}
      {...props}
    >
      {loading ? (
        <LoadingSpinner size={20} />
      ) : (
        <>
          {icon && <ButtonIcon icon={icon} className={iconClassNames} />}
          <span className="text-center">{children}</span>
        </>
      )}
    </button>
  );

  return tooltip ? (
    <Tooltip content={tooltip}>
      <span
        tabIndex={disabled ? 0 : -1}
        title={disabled ? tooltip : undefined}
        className="inline-block focus:outline-none"
      >
        {buttonElement}
      </span>
    </Tooltip>
  ) : (
    buttonElement
  );
});

Button.displayName = 'Button';
